import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Ticket, CreditCard, AlertTriangle, TrendingUp, Clock } from 'lucide-react';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;
  if (!data) return null;

  const stats = [
    { label: 'Total Residents', value: data.total_residents, icon: Users, color: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Open Tickets', value: data.open_tickets, icon: AlertTriangle, color: 'bg-orange-50 text-orange-600', border: 'border-orange-100' },
    { label: 'Total Collected', value: `₹${data.total_collected?.toLocaleString()}`, icon: TrendingUp, color: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Pending Dues', value: `₹${data.total_pending?.toLocaleString()}`, icon: CreditCard, color: 'bg-red-50 text-red-600', border: 'border-red-100' },
  ];

  const statusColor = (s) => {
    const map = { open: 'bg-amber-50 text-amber-700 border-amber-100', in_progress: 'bg-blue-50 text-blue-700 border-blue-100', resolved: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
    return map[s] || 'bg-slate-100 text-slate-600';
  };
  const priorityColor = (p) => {
    const map = { urgent: 'bg-red-50 text-red-700 border-red-100', normal: 'bg-slate-100 text-slate-600', high: 'bg-orange-50 text-orange-700 border-orange-100' };
    return map[p] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of your community</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <Card key={i} className={`border-slate-100 card-hover`} data-testid={`stat-card-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                  <s.icon size={18} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-['Outfit']">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tickets */}
        <Card className="border-slate-100">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Ticket size={16} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-900 font-['Outfit']">Recent Tickets</h3>
            </div>
            <div className="space-y-3">
              {data.recent_tickets?.length === 0 && <p className="text-sm text-slate-400">No tickets yet</p>}
              {data.recent_tickets?.map(t => (
                <div key={t.ticket_id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50/80 hover:bg-slate-50 transition-colors" data-testid={`recent-ticket-${t.ticket_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">by {t.created_by_name || 'Unknown'}</p>
                  </div>
                  <Badge className={`text-[10px] ${statusColor(t.status)}`}>{t.status?.replace('_', ' ')}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Announcements */}
        <Card className="border-slate-100">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-900 font-['Outfit']">Recent Announcements</h3>
            </div>
            <div className="space-y-3">
              {data.recent_announcements?.length === 0 && <p className="text-sm text-slate-400">No announcements yet</p>}
              {data.recent_announcements?.map(a => (
                <div key={a.announcement_id} className="p-3 rounded-lg bg-slate-50/80 hover:bg-slate-50 transition-colors" data-testid={`recent-ann-${a.announcement_id}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900 flex-1 truncate">{a.title}</p>
                    <Badge className={`text-[10px] ${priorityColor(a.priority)}`}>{a.priority}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.content}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
